import React, { useEffect } from "react";
import Plot from "react-plotly.js";

import { useError } from "../../../contexts/ErrorContext";
import { useConnectorFTQ } from "../../../hooks/useFulfillmentsAnalytics";
import { ConnectorFTQData } from "../../../utils/fulfillmentsAnalytics";

import { Box, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";

interface FulfillmentsConnectorFTQChartProps {
  dateStart?: string;
  dateEnd?: string;
}

const FulfillmentsConnectorFTQChart: React.FC<
  FulfillmentsConnectorFTQChartProps
> = (props) => {
  const { showError } = useError();
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;

  const { dateStart = undefined, dateEnd = undefined } = props;

  const {
    data: connectorFTQData,
    error,
    isLoading,
  } = useConnectorFTQ(dateStart, dateEnd) as {
    data: ConnectorFTQData[] | undefined;
    error: Error | null;
    isLoading: boolean;
  };

  useEffect(() => {
    if (error) {
      showError(error?.message || `Failed to get Connector FTQ`);
    }
  }, [error, showError]);

  return (
    <Box
      sx={{
        display: "block",
        width: "100%",
        minHeight: "500px",
      }}
    >
      {isLoading ? (
        <CircularProgress
          size={20}
          sx={{ marginRight: 1, verticalAlign: "middle" }}
        />
      ) : connectorFTQData && connectorFTQData.length > 0 ? (
        <Plot
          config={{
            displaylogo: false,
            modeBarButtonsToRemove: ["select2d", "lasso2d"],
            responsive: true,
          }}
          data={[
            {
              x: connectorFTQData.map((item) => {
                const date = new Date(item.week_start_date);
                return date.toISOString().split("T")[0];
              }),
              y: connectorFTQData.map(
                (item) => item.count_connector_fulfillments
              ),
              type: "bar",
              name: "Total Connector Fulfillments",
              marker: { color: "#2A9D8F" },
              hovertemplate: `<b>Total Connector Fulfillments:</b> %{y}<extra></extra>`,
            },
            {
              x: connectorFTQData.map((item) => {
                const date = new Date(item.week_start_date);
                return date.toISOString().split("T")[0];
              }),
              y: connectorFTQData.map(
                (item) => item.pct_connector_ftq_succeeded
              ),
              type: "scatter",
              mode: "lines+markers",
              name: "Connector FTQ Success Rate (%)",
              marker: { color: "#E76F51", size: 4 },
              line: { color: "#E76F51", width: 2 },
              yaxis: "y2",
              hoverinfo: "none",
              hovertemplate: `<b>Connector FTQ Success Rate:</b> %{y}%<br><b>Connector FTQ Success Count:</b> %{customdata[0]}<extra></extra>`,
              customdata: connectorFTQData.map((item) => [
                item.count_connector_ftq_succeeded,
              ]),
            },
          ]}
          layout={{
            title: {
              text: "Connector FTQ (First Time Quality)",
              font: {
                color: primaryColor,
              },
            },
            legend: {
              orientation: "h",
              x: 0.5,
              y: 1.1,
              xanchor: "center",
              yanchor: "top",
            },
            xaxis: {
              title: {
                text: "Week Start Date",
                standoff: 15,
              },
              showgrid: false,
              tickangle: -45,
              tickfont: {
                size: 12,
              },
            },
            yaxis: {
              title: "Total Connector Fulfillments",
              showgrid: true,
              gridcolor: "#D3D3D3",
              side: "left",
            },
            yaxis2: {
              title: "Connector FTQ Success Rate (%)",
              overlaying: "y",
              side: "right",
              showgrid: false,
              rangemode: "tozero",
            },
            hovermode: "x unified",
            barmode: "group",
            margin: {
              b: 100,
              t: 80,
              l: 60,
              r: 60,
            },
            hoverlabel: {
              bgcolor: "#FFF",
              font: { color: "#264653" },
              bordercolor: "#E76F51",
              align: "left",
            },
            font: {
              family: '"Noto Sans", serif',
              size: 14,
            },
          }}
          style={{ width: "100%", height: "100%" }}
        />
      ) : (
        <p>No data available</p>
      )}
    </Box>
  );
};

export default FulfillmentsConnectorFTQChart;
